import type {
  IApiProductSettingsResponse,
  IModelDetailResponse,
  ISettings,
} from './types'
import { getChromeDataApi, getJamsBackendApi, getModelDataApi } from '@/utils'

const DATAHUB_HUB_BASE =
  'https://api.data-enhancement-hub.sodasoft.io/hub'

const getDataHubBase = (): string => {
  const modelApi = getModelDataApi()?.trim()
  if (modelApi) {
    return modelApi.replace(/\/v-data\/?$/, '') || DATAHUB_HUB_BASE
  }
  return DATAHUB_HUB_BASE
}

const getDataHubVDataApi = (): string => {
  const modelApi = getModelDataApi()?.trim()
  if (modelApi) return modelApi.replace(/\/$/, '')
  return `${DATAHUB_HUB_BASE}/v-data`
}

export const getProductSettings = async (
  accountId: string,
  productId: string,
): Promise<IApiProductSettingsResponse> => {
  const path = `unauth/account/products/${accountId}/${productId}`
  const endpoint = getJamsBackendApi()

  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Could not fetch products settings.')
    }

    console.log('Response: ', response)

    const responseJson: unknown = await response.json()

    console.log('Response JSON: ', responseJson)

    return {
      success: true,
      data: (responseJson as ISettings) ?? null,
    }
  } catch (err) {
    console.error(err)

    return {
      success: false,
      data: null,
    }
  }
}

export const fetchVehicleDetailByModel = async (
  year: number,
  make: string,
  model: string,
): Promise<IModelDetailResponse | null> => {
  try {
    const hybridApi = getModelDataApi()
    const path = `${year}/${make}/${model}`
    const base =
      hybridApi && hybridApi.trim().length > 0 ? hybridApi : getChromeDataApi()
    const url = `${base}/${path}`
    console.log('Model fetch URL:', url)
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status} - ${response.statusText}`,
      )
    }

    const data = await response.json()

    if (!data || typeof data !== 'object') {
      throw new Error('Unexpected response format')
    }

    const isHybrid = data && (data.trimsCount != null || data.shared != null)
    if (!isHybrid) {
      return data
    }

    const mergeCats = (
      shared: Record<string, { items: string[] }> = {},
      specific: Record<string, { items: string[] }> = {},
    ) => {
      const out: Record<string, { items: string[] }> = {}
      const keys = new Set<string>([
        ...Object.keys(shared || {}),
        ...Object.keys(specific || {}),
      ])
      for (const k of keys) {
        const a = (shared?.[k]?.items || []) as string[]
        const b = (specific?.[k]?.items || []) as string[]
        const merged = Array.from(new Set<string>([...a, ...b]))
        if (merged.length > 0) out[k] = { items: merged }
      }
      return out
    }

    const trims = (data.trims || []).map((t: RawTrimData) => ({
      trim: t.trim || t.styleName || '',
      styleId: String(t.id || t.styleId || ''),
      styleName: t.styleName || t.trim || '',
      price: String(t.baseMsrp ?? ''),
      img: t.img || '',
      year: String(data.year ?? year),
      make: data.make || make,
      model: data.model || model,
      images: {},
      standardEquipment: mergeCats(
        data.shared?.standardEquipment,
        t.standardEquipment,
      ),
      availableOptions: t.availableOptions || {},
      consumerInformation: mergeCats(
        data.shared?.consumerInformation,
        t.consumerInformation,
      ),
      technicalSpecification: mergeCats(
        data.shared?.technicalSpecification,
        t.technicalSpecification,
      ),
    }))

    const adapted = {
      year: String(data.year ?? year),
      make: data.make || make,
      model: data.model || model,
      trims,
    }
    return adapted as IModelDetailResponse
  } catch (error: unknown) {
    if (error instanceof TypeError) {
      console.error('Network error or CORS issue:', error.message)
    } else if (error instanceof Error) {
      console.error(
        'An error occurred while fetching vehicle detail:',
        error.message,
      )
    } else {
      console.error(
        'An error occurred while fetching vehicle detail:',
        String(error),
      )
    }
    return null
  }
}

import type { ModelData } from '@/types'

export const fetchDataHubModel = async (
  year: number,
  make: string,
  model: string,
): Promise<ModelData | null> => {
  try {
    const encodedMake = encodeURIComponent(make.trim())
    const encodedModel = encodeURIComponent(model.trim())
    const url = `${getDataHubVDataApi()}/${year}/${encodedMake}/${encodedModel}`
    console.log('Fetching from DataHub:', url)

    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }

    const data = await response.json()

    if (data) {
      if (data.trims) {
        data.trims = data.trims.map((trim: RawTrimData, index: number) => ({
          ...trim,
          id: trim.id || trim.styleId || `trim-${index}`,
          baseMsrp: trim.baseMsrp || trim.price,
          colors: extractColors(trim),
          images: extractImages(trim),
        }))
      }

      return data
    }

    return null
  } catch (error) {
    console.error('Error fetching from DataHub:', error)
    return null
  }
}

interface RawTrimData {
  id?: string | number
  trim?: string
  styleName?: string
  styleId?: string | number
  baseMsrp?: number
  price?: number
  img?: string
  images?: unknown
  availableOptions?: Record<string, { items: string[] }>
  standardEquipment?: Record<string, { items: string[] }>
  consumerInformation?: Record<string, { items: string[] }>
  technicalSpecification?: Record<string, { items: string[] }>
  [key: string]: unknown
}

interface ColorData {
  colorName: string
  hex: string
  rgb: string
}

interface ColorizedImage {
  colorValue?: string
  colorName?: string
  colorRgb?: string
  url?: string
  image?: string
  [key: string]: unknown
}

const extractColors = (trim: RawTrimData): ColorData[] => {
  const colors: ColorData[] = []
  const trimImages = trim.images as { colorized?: ColorizedImage[] } | undefined
  const colorizedImages = trimImages?.colorized || []

  const colorMap = new Map<string, ColorData>()

  colorizedImages.forEach((img: ColorizedImage) => {
    if (img.colorValue && !colorMap.has(img.colorValue)) {
      colorMap.set(img.colorValue, {
        colorName: img.colorName || 'Unknown',
        hex: img.colorValue,
        rgb: img.colorRgb || hexToRgb(img.colorValue),
      })
    }
  })

  const colorOptions = trim.availableOptions?.['Exterior Paint']?.items || []
  colorOptions.forEach((colorOption: string) => {
    const colorName = colorOption.split(':')[0]?.trim()
    if (
      colorName &&
      !Array.from(colorMap.values()).some(
        (c: ColorData) => c.colorName === colorName,
      )
    ) {
      colors.push({
        colorName,
        hex: '#808080',
        rgb: 'rgb(128, 128, 128)',
      })
    }
  })

  return [...Array.from(colorMap.values()), ...colors]
}

interface TrimImage {
  url: string
  colorHex?: string
  colorName?: string
  [key: string]: unknown
}

const extractImages = (trim: RawTrimData): TrimImage[] => {
  const images = []

  if (trim.img) {
    images.push({
      url: trim.img,
      type: 'main',
      colorHex: undefined,
    })
  }

  const trimImages = trim.images as { colorized?: ColorizedImage[] } | undefined
  const colorizedImages = trimImages?.colorized || []
  colorizedImages.forEach((img: ColorizedImage) => {
    if (img.url || img.image) {
      images.push({
        url: img.url || img.image || '',
        type: 'colorized',
        colorHex: img.colorValue,
        colorName: img.colorName,
      })
    }
  })

  return images
}

const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result) {
    const r = parseInt(result[1], 16)
    const g = parseInt(result[2], 16)
    const b = parseInt(result[3], 16)
    return `rgb(${r}, ${g}, ${b})`
  }
  return 'rgb(128, 128, 128)'
}

export interface Year {
  modelYear: number
}

export interface Division {
  id: number
  name: string
}

export interface Model {
  id: number
  name: string
}

interface YearsResponse {
  years: Array<number | string>
}

interface ModelsByMakeResponse {
  year?: number | string
  make?: string
  models?: string[]
}

export const fetchAvailableYears = async (): Promise<number[]> => {
  try {
    const response = await fetch(getDataHubVDataApi())
    if (!response.ok) {
      throw new Error(`Failed to fetch years: ${response.status}`)
    }
    const data: YearsResponse | Array<number | string> = await response.json()
    const years = Array.isArray(data) ? data : (data.years ?? [])
    return years.map(y => Number(y)).filter(Boolean).sort((a, b) => b - a)
  } catch (error) {
    console.error('Error fetching years:', error)
    return []
  }
}

export const fetchAvailableMakes = async (
  year: number,
): Promise<{ id: number; name: string }[]> => {
  try {
    const response = await fetch(
      `${getDataHubBase()}/chrome/makes?modelYear=${year}`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch makes: ${response.status}`)
    }
    const data: Division[] = await response.json()
    return data
      .filter(d => d.id && d.name)
      .map(d => ({
        id: d.id,
        name: d.name,
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  } catch (error) {
    console.error('Error fetching makes:', error)
    return []
  }
}

export const fetchAvailableModels = async (
  year: number,
  make: string,
): Promise<string[]> => {
  try {
    const encodedMake = encodeURIComponent(make.trim())
    const response = await fetch(
      `${getDataHubVDataApi()}/${year}/${encodedMake}`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch models: ${response.status}`)
    }
    const data: ModelsByMakeResponse | string[] = await response.json()
    const models = Array.isArray(data) ? data : (data.models ?? [])
    return models
      .filter((m): m is string => Boolean(m))
      .sort((a, b) => a.localeCompare(b))
  } catch (error) {
    console.error('Error fetching models:', error)
    return []
  }
}
